import React, { useState, useEffect } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from './supabaseClient'
import StatsHeader from './StatsHeader'
import AccountListView from './AccountListView'
import AssignPromo from './AssignPromo'
import QuickEntry from './QuickEntry'
import AddAccountToPromo from './AddAccountToPromo'
import AddAccountModal from './AddAccountModal'
import ActivityFeed from './ActivityFeed'
import QuarterManagement from './QuarterManagement'
import AccountNotes from './AccountNotes'
import NotificationSettings from './NotificationSettings'
import Toast from './Toast'
import ChangePassword from './ChangePassword'
import RepBreakdown from './RepBreakdown'
import QuarterReset from './QuarterReset'

const Dashboard = () => {
  const { user, signOut, isAdmin } = useAuth()
  
  // Data
  const [accounts, setAccounts] = useState([])
  const [accountsWithPromos, setAccountsWithPromos] = useState([])
  const [accountProgress, setAccountProgress] = useState({})
  const [accountNotes, setAccountNotes] = useState({})
  const [loading, setLoading] = useState(true)
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState('')
  const [territoryFilter, setTerritoryFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  
  // Modals
  const [showQuickEntry, setShowQuickEntry] = useState(false)
  const [showAssignPromo, setShowAssignPromo] = useState(false)
  const [showAddToPromo, setShowAddToPromo] = useState(false)
  const [showAddAccount, setShowAddAccount] = useState(false)
  const [showQuarterManagement, setShowQuarterManagement] = useState(false)
  const [showAccountNotes, setShowAccountNotes] = useState(false)
  const [showNotificationSettings, setShowNotificationSettings] = useState(false)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [newAccountName, setNewAccountName] = useState('')
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [selectedAccountPromo, setSelectedAccountPromo] = useState(null)
  const [showRepBreakdown, setShowRepBreakdown] = useState(false)
  const [myOpportunitiesOnly, setMyOpportunitiesOnly] = useState(false)
  const [showQuarterReset, setShowQuarterReset] = useState(false)
  
  // Quarter data
  const [activeQuarter, setActiveQuarter] = useState(null)
  const [quarterProgress, setQuarterProgress] = useState(50)
  const [activityRefreshKey, setActivityRefreshKey] = useState(0)
  
  // Toast notifications
  const [toasts, setToasts] = useState([])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
      
      if (e.key === '/') {
        e.preventDefault()
        document.querySelector('input[type="text"]')?.focus()
      }
      
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault()
        setSelectedAccount(null)
        setSelectedAccountPromo(null)
        setShowQuickEntry(true)
      }
      
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault()
        fetchAccounts()
        showToast('Refreshed!', 'success')
      }
      
      if (e.key === 'Escape') {
        setShowQuickEntry(false)
        setShowAssignPromo(false)
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [])

  useEffect(() => {
    fetchAccounts()
    fetchActiveQuarter()
  }, [])

  const fetchAccounts = async () => {
    try {
      const { data: accountPromos, error: promoError } = await supabase
        .from('account_promos')
        .select(`
          account_id,
          target_units,
          terms,
          assigned_date,
          promo_id,
          promos (
            id,
            promo_name,
            promo_code,
            discount
          )
        `)
        .order('assigned_date', { ascending: false })

      if (promoError) throw promoError

      const latestPromosByAccount = {}
      accountPromos.forEach(ap => {
        if (!latestPromosByAccount[ap.account_id]) {
          latestPromosByAccount[ap.account_id] = ap
        }
      })

      const accountIdsWithPromos = Object.keys(latestPromosByAccount)

      if (accountIdsWithPromos.length === 0) {
        setAccountsWithPromos([])
        setAccountProgress({})
        setAccountNotes({})
        setLoading(false)
        return
      }

      const { data: accountsData, error: accountsError } = await supabase
        .from('accounts')
        .select('*')
        .in('id', accountIdsWithPromos)
        .order('account_name')

      if (accountsError) throw accountsError

      const enrichedAccounts = accountsData.map(account => {
        const accountPromo = latestPromosByAccount[account.id]
        return {
          ...account,
          promo_name: accountPromo?.promos?.promo_name,
          discount: accountPromo?.promos?.discount,
          terms: accountPromo?.terms,
          target_units: accountPromo?.target_units,
          promo_id: accountPromo?.promos?.id || accountPromo?.promo_id,
          promoData: accountPromo
        }
      })

      setAccountsWithPromos(enrichedAccounts)
      await calculateAllProgress(enrichedAccounts)
      await fetchAllAccountNotes(accountIdsWithPromos)

    } catch (error) {
      console.error('Error fetching accounts:', error)
      showToast('Failed to load accounts', 'error')
    } finally {
      setLoading(false)
    }
  }

  const fetchAllAccountNotes = async (accountIds) => {
    try {
      const { data: notes, error } = await supabase
        .from('account_notes')
        .select(`
          id,
          account_id,
          note,
          created_at,
          reps (name)
        `)
        .in('account_id', accountIds)
        .order('created_at', { ascending: false })

      if (error) throw error

      // Group by account_id and take only the most recent
      const notesMap = {}
      notes?.forEach(note => {
        if (!notesMap[note.account_id]) {
          notesMap[note.account_id] = note
        }
      })

      setAccountNotes(notesMap)
    } catch (error) {
      console.error('Error fetching notes:', error)
    }
  }

  const calculateAllProgress = async (accountsList) => {
    const progressMap = {}
    const updatedAccounts = [...accountsList]
    
    for (let i = 0; i < updatedAccounts.length; i++) {
      const account = updatedAccounts[i]
      try {
        if (!account.promo_id) {
          progressMap[account.id] = { progress: 0, units_sold: 0 }
          continue
        }

        const { data: transactions } = await supabase
          .from('transactions')
          .select('units_sold')
          .eq('account_id', account.id)
          .eq('promo_id', account.promo_id)

        const totalUnits = transactions?.reduce((sum, t) => sum + t.units_sold, 0) || 0
        const progress = account.target_units ? Math.round((totalUnits / account.target_units) * 100) : 0
        
        progressMap[account.id] = { progress, units_sold: totalUnits }
        updatedAccounts[i] = { ...account, units_sold: totalUnits }
      } catch (error) {
        progressMap[account.id] = { progress: 0, units_sold: 0 }
      }
    }
    
    setAccountProgress(progressMap)
    setAccountsWithPromos(updatedAccounts)
  }

  const fetchActiveQuarter = async () => {
    try {
      const { data, error } = await supabase
        .from('quarters')
        .select('*')
        .eq('is_active', true)
        .single()

      if (error && error.code !== 'PGRST116') {
        console.log('Quarters table not found, using defaults')
        return
      }

      if (data) {
        setActiveQuarter(data)
        
        const start = new Date(data.start_date)
        const end = new Date(data.end_date)
        const now = new Date()
        
        const totalDays = (end - start) / (1000 * 60 * 60 * 24)
        const daysPassed = (now - start) / (1000 * 60 * 60 * 24)
        
        const progress = Math.min(100, Math.max(0, Math.round((daysPassed / totalDays) * 100)))
        setQuarterProgress(progress)
      }
    } catch (error) {
      console.error('Error fetching quarter:', error)
    }
  }

  const showToast = (message, type = 'success') => {
    const id = Date.now()
    setToasts(prev => [...prev, { id, message, type }])
  }

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }

  const territories = ['all', 'Vancouver', 'Richmond', 'Kelowna']

  const filteredAccounts = accountsWithPromos.filter(account => {
    const matchesSearch = 
      account.account_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.territory?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesTerritory = territoryFilter === 'all' || account.territory?.includes(territoryFilter)
    
    let matchesStatus = true
    if (statusFilter !== 'all') {
      const progress = accountProgress[account.id]?.progress || 0
      if (statusFilter === 'behind_pace') {
        matchesStatus = progress < 100 && progress < (quarterProgress - 10)
      } else if (statusFilter === 'on_pace') {
        matchesStatus = progress < 100 && progress >= (quarterProgress - 10)
      } else if (statusFilter === 'met') {
        matchesStatus = progress >= 100
      }
    }
    
    const myUnits = accountProgress[account.id]?.my_units || 0
    const matchesMyOpportunities = !myOpportunitiesOnly || myUnits === 0
    
    return matchesSearch && matchesTerritory && matchesStatus && matchesMyOpportunities
  }).sort((a, b) => {
    const progressA = accountProgress[a.id]?.progress || 0
    const progressB = accountProgress[b.id]?.progress || 0
    
    const behindA = progressA - quarterProgress
    const behindB = progressB - quarterProgress
    
    return behindA - behindB
  })

  const exportToCSV = async (territory = 'all') => {
    try {
      const accountsToExport = territory === 'all' 
        ? accountsWithPromos 
        : accountsWithPromos.filter(a => a.territory === territory)

      const exportData = accountsToExport.map(account => {
        const progressData = accountProgress[account.id] || { progress: 0, units_sold: 0 }
        const progress = progressData.progress || 0
        const unitsSold = progressData.units_sold || account.units_sold || 0
        
        return {
          'Account Name': account.account_name,
          'Territory': account.territory,
          'Promo': account.promo_name || 'Not Assigned',
          'Discount': account.discount ? `${account.discount}%` : 'N/A',
          'Terms': account.terms || 'N/A',
          'Target': account.target_units || 0,
          'Units Sold': unitsSold,
          'Progress': `${progress}%`,
          'Status': progress >= 100 ? 'Met' : progress >= 75 ? 'On Track' : 'Behind'
        }
      })

      const headers = Object.keys(exportData[0])
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => 
          headers.map(header => `"${row[header]}"`).join(',')
        )
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      const filename = territory === 'all' 
        ? 'PromoSync-All-Territories.csv' 
        : `PromoSync-${territory}.csv`
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      showToast('Exported successfully!', 'success')
    } catch (error) {
      console.error('Error exporting:', error)
      showToast('Export failed. Please try again.', 'error')
    }
  }

  const handleQuickLog = (account, promoData) => {
    setSelectedAccount(account)
    setSelectedAccountPromo(promoData)
    setShowQuickEntry(true)
  }

  const handleAssignPromo = (account, currentPromo = null) => {
    setSelectedAccount(account)
    setSelectedAccountPromo(currentPromo)
    setShowAssignPromo(true)
  }

  const handleViewRepBreakdown = (account, promo) => {
    setSelectedAccount(account)
    setSelectedAccountPromo(promo)
    setShowRepBreakdown(true)
  }

  const handleRefresh = () => {
    fetchAccounts()
    setActivityRefreshKey(prev => prev + 1)
    showToast('Refreshed!', 'success')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800">
      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      {/* Header */}
      <header className="bg-gray-800/80 backdrop-blur-sm border-b border-gray-700/50 sticky top-0 z-20 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-white tracking-tight">
                PromoSync
              </h1>
              <p className="text-sm text-gray-400">Welcome back, {user?.name}</p>
            </div>
            <div className="flex items-center space-x-3">
              {/* Quarter Info */}
              {activeQuarter && (
                <div className="hidden md:flex items-center space-x-2 text-sm bg-gray-700/50 px-3 py-1.5 rounded-lg">
                  <span className="text-gray-400">📅</span>
                  <span className="text-white font-medium">{activeQuarter.name}</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-blue-400">{quarterProgress}% complete</span>
                </div>
              )}
              
              <span className="hidden lg:block text-xs text-gray-500 bg-gray-700/50 px-2 py-1 rounded">
                Press <kbd className="px-1.5 py-0.5 bg-gray-600 rounded text-gray-300 font-mono text-xs">/</kbd> to search
              </span>
              {isAdmin && (
                <>
                  <button
                    onClick={() => setShowQuarterManagement(true)}
                    className="hidden sm:flex items-center space-x-1 px-3 py-1.5 bg-gray-700/80 hover:bg-gray-600 text-white text-sm rounded-lg transition"
                  >
                    <span>📅</span>
                    <span>Quarters</span>
                  </button>
                  <button
                    onClick={() => setShowQuarterReset(true)}
                    className="hidden sm:flex items-center space-x-1 px-3 py-1.5 bg-red-700/80 hover:bg-red-600 text-white text-sm rounded-lg transition"
                  >
                    <span>🔄</span>
                    <span>End Quarter</span>
                  </button>
                  <span className="px-3 py-1 bg-blue-600/90 text-white text-sm rounded-full font-medium">
                    Admin
                  </span>
                </>
              )}
              
              {/* Notification Settings */}
              <button
                onClick={() => setShowNotificationSettings(true)}
                className="p-2 bg-gray-700/80 hover:bg-gray-600 text-white rounded-lg transition"
                title="Notification Settings"
              >
                🔔
              </button>
              <button
                onClick={() => setShowChangePassword(true)}
                className="p-2 bg-gray-700/80 hover:bg-gray-600 text-white rounded-lg transition"
                title="Change Password"
              >
                🔐
              </button>
              <button
                onClick={signOut}
                className="px-4 py-2 bg-gray-700/80 hover:bg-gray-600 text-white rounded-lg transition font-medium text-sm"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Stats Dashboard */}
        <StatsHeader onFilterBehindPace={() => setStatusFilter('behind_pace')} />

        {/* Quick Actions Bar - Mobile Responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 px-2 sm:px-4">
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            {/* Add to Promo Button - SECONDARY (Outlined) - Touch Friendly */}
            <button
              onClick={() => setShowAddToPromo(true)}
              className="bg-transparent hover:bg-gray-700/50 border-2 border-gray-600 hover:border-gray-500 text-gray-200 hover:text-white font-medium py-3 px-6 rounded-lg transition-all duration-150 flex items-center justify-center space-x-2 min-h-[44px]"
            >
              <span className="text-lg">➕</span>
              <span>Add to Promo</span>
            </button>

            {/* Quick Log Units Button - PRIMARY (Hero) - Touch Friendly */}
            <button
              onClick={() => {
                setSelectedAccount(null)
                setSelectedAccountPromo(null)
                setShowQuickEntry(true)
              }}
              className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold py-3 px-6 rounded-lg transition-all duration-150 flex items-center justify-center space-x-2 shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/40 ring-2 ring-blue-600/20 min-h-[44px]"
            >
              <span>Quick Log Units</span>
              <kbd className="hidden lg:inline ml-2 px-1.5 py-0.5 bg-blue-700 rounded text-xs font-mono">N</kbd>
            </button>
          </div>
          
          <div className="flex gap-3 w-full sm:w-auto">
            {/* Utility Actions - Touch Friendly */}
            <button
              onClick={handleRefresh}
              className="flex-1 sm:flex-none bg-gray-700/80 hover:bg-gray-600 border border-gray-600/50 text-gray-200 font-medium py-3 px-5 rounded-lg transition-all duration-150 flex items-center justify-center space-x-2 min-h-[44px]"
            >
              <span className="text-lg">🔄</span>
              <span>Refresh</span>
              <kbd className="hidden lg:inline ml-2 px-1.5 py-0.5 bg-gray-600 rounded text-xs font-mono">R</kbd>
            </button>

            {/* Export Dropdown - Mobile Friendly */}
            <div className="relative group flex-1 sm:flex-none">
              <button className="w-full bg-emerald-600/90 hover:bg-emerald-700 text-white font-medium py-3 px-5 rounded-lg transition-all duration-150 flex items-center justify-center space-x-2 shadow-md shadow-emerald-600/20 hover:shadow-lg min-h-[44px]">
                <span className="text-lg">📥</span>
                <span>Export</span>
                <span className="text-xs">▼</span>
              </button>
              
              <div className="absolute right-0 mt-2 w-52 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-30 overflow-hidden">
                <button
                  onClick={() => exportToCSV('all')}
                  className="w-full text-left px-4 py-3 text-white hover:bg-gray-700 transition-colors flex items-center space-x-2 min-h-[44px]"
                >
                  <span>📊</span>
                  <span>All Territories</span>
                </button>
                <div className="border-t border-gray-700"></div>
                <button
                  onClick={() => exportToCSV('Kelowna')}
                  className="w-full text-left px-4 py-3 text-white hover:bg-gray-700 transition-colors flex items-center space-x-2 min-h-[44px]"
                >
                  <span>📍</span>
                  <span>Kelowna Only</span>
                </button>
                <button
                  onClick={() => exportToCSV('Richmond')}
                  className="w-full text-left px-4 py-3 text-white hover:bg-gray-700 transition-colors flex items-center space-x-2 min-h-[44px]"
                >
                  <span>📍</span>
                  <span>Richmond Only</span>
                </button>
                <button
                  onClick={() => exportToCSV('Vancouver')}
                  className="w-full text-left px-4 py-3 text-white hover:bg-gray-700 transition-colors flex items-center space-x-2 min-h-[44px]"
                >
                  <span>📍</span>
                  <span>Vancouver Only</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-5 mb-6 shadow-lg">
          <div className="flex flex-col lg:flex-row gap-4 mb-4">
            {/* Search Bar */}
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-gray-400 text-lg">🔍</span>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search accounts..."
                  className="w-full pl-12 pr-12 py-3 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition"
                  >
                    <span className="text-lg">✕</span>
                  </button>
                )}
              </div>
            </div>

            {/* Territory Filter */}
            <div className="w-full lg:w-56">
              <select
                value={territoryFilter}
                onChange={(e) => setTerritoryFilter(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
              >
                {territories.map(territory => (
                  <option key={territory} value={territory}>
                    {territory === 'all' ? '🌍 All Territories' : `📍 ${territory}`}
                  </option>
                ))}
              </select>
            </div>

            {/* My Opportunities Filter */}
            <button
              onClick={() => setMyOpportunitiesOnly(!myOpportunitiesOnly)}
              className={`px-4 py-3 rounded-lg text-sm font-medium transition ${
                myOpportunitiesOnly 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-700/50 border border-gray-600/50 text-gray-300 hover:bg-gray-600/50'
              }`}
            >
              {myOpportunitiesOnly ? '✓ My Opportunities' : '🎯 My Opportunities'}
            </button>
          </div>

          {/* Status Filter Buttons */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                statusFilter === 'all'
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700 border border-gray-600/50'
              }`}
            >
              All Accounts
            </button>
            <button
              onClick={() => setStatusFilter('behind_pace')}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-1.5 ${
                statusFilter === 'behind_pace'
                  ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700 border border-gray-600/50'
              }`}
            >
              <span>⚠️</span>
              <span>Behind Pace</span>
            </button>
            <button
              onClick={() => setStatusFilter('on_pace')}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-1.5 ${
                statusFilter === 'on_pace'
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700 border border-gray-600/50'
              }`}
            >
              <span>📊</span>
              <span>On Pace</span>
            </button>
            <button
              onClick={() => setStatusFilter('met')}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-1.5 ${
                statusFilter === 'met'
                  ? 'bg-green-600 text-white shadow-lg shadow-green-600/30'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700 border border-gray-600/50'
              }`}
            >
              <span>🟢</span>
              <span>Target Met</span>
            </button>
          </div>

          {/* Results count */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">
              Showing <span className="font-semibold text-white">{filteredAccounts.length}</span> accounts on promos
            </span>
            {(searchTerm || territoryFilter !== 'all' || statusFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('')
                  setTerritoryFilter('all')
                  setStatusFilter('all')
                }}
                className="text-blue-400 hover:text-blue-300 transition flex items-center space-x-1 text-sm"
              >
                <span>✕</span>
                <span>Clear filters</span>
              </button>
            )}
          </div>
        </div>

        {/* Accounts Display - List View Only */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-blue-500"></div>
            <p className="text-gray-400 mt-4 text-lg">Loading accounts...</p>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="text-center py-16 bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl">
            <span className="text-6xl mb-4 block">📭</span>
            <p className="text-gray-400 text-lg mb-2 font-medium">
              {searchTerm || territoryFilter !== 'all' || statusFilter !== 'all'
                ? 'No accounts match your filters' 
                : 'No accounts on promos yet'}
            </p>
            <p className="text-gray-500 text-sm mb-4">
              Click "Add to Promo" to get started
            </p>
          </div>
        ) : (
          <AccountListView
            accounts={filteredAccounts}
            accountProgress={accountProgress}
            accountNotes={accountNotes}
            onAssignPromo={handleAssignPromo}
            onQuickLog={handleQuickLog}
            onViewNotes={(account) => {
              setSelectedAccount(account)
              setShowAccountNotes(true)
            }}
            quarterProgress={quarterProgress}
            onViewRepBreakdown={handleViewRepBreakdown}
          />
        )}

        {/* Activity Feed - Below accounts on desktop, hidden on mobile */}
        <div className="hidden lg:block mt-8">
          <ActivityFeed key={activityRefreshKey} limit={10} />
        </div>
      </main>

      {/* Modals */}
      {showQuickEntry && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 md:p-4 overflow-y-auto">
          <div className="bg-gray-900 md:rounded-xl w-full md:max-w-2xl min-h-screen md:min-h-0 md:my-8 shadow-2xl border-0 md:border border-gray-700/50">
            <div className="p-4 md:p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl md:text-2xl font-semibold text-white">Quick Log Units</h2>
                <button
                  onClick={() => setShowQuickEntry(false)}
                  className="text-gray-400 hover:text-white transition text-2xl w-11 h-11 flex items-center justify-center rounded-lg hover:bg-gray-800 min-h-[44px] min-w-[44px]"
                >
                  ✕
                </button>
              </div>
              <QuickEntry
                preSelectedAccount={selectedAccount}
                onSuccess={() => {
                  handleRefresh()
                  setShowQuickEntry(false)
                  showToast('Units logged successfully!', 'success')
                }}
              />
            </div>
          </div>
        </div>
      )}

      {showAssignPromo && selectedAccount && (
        <AssignPromo
          account={selectedAccount}
          currentPromo={selectedAccountPromo}
          onClose={() => {
            setShowAssignPromo(false)
            setSelectedAccount(null)
            setSelectedAccountPromo(null)
          }}
          onSuccess={() => {
            handleRefresh()
            showToast('Promo assigned successfully!', 'success')
          }}
        />
      )}

      {/* Add Account to Promo Modal */}
      {showAddToPromo && (
        <AddAccountToPromo
          onClose={() => setShowAddToPromo(false)}
          onSuccess={() => {
            setShowAddToPromo(false)
            handleRefresh()
            showToast('Account added to promo!', 'success')
          }}
          onAddNew={(accountName) => {
            setNewAccountName(accountName)
            setShowAddAccount(true)
          }}
        />
      )}

      {/* Add Account Modal */}
      {showAddAccount && (
        <AddAccountModal
          accountName={newAccountName}
          onClose={() => {
            setShowAddAccount(false)
            setNewAccountName('')
          }}
          onSuccess={(newAccount) => {
            setShowAddAccount(false)
            setNewAccountName('')
            handleRefresh()
            showToast('Account added successfully!', 'success')
          }}
        />
      )}

      {/* Quarter Management Modal (Admin Only) */}
      {showQuarterManagement && (
        <QuarterManagement
          onClose={() => {
            setShowQuarterManagement(false)
            fetchActiveQuarter()
          }}
        />
      )}

      {/* Account Notes Modal */}
      {showAccountNotes && selectedAccount && (
        <AccountNotes
          account={selectedAccount}
          onClose={() => {
            setShowAccountNotes(false)
            setSelectedAccount(null)
            handleRefresh()
          }}
        />
      )}

      {/* Notification Settings Modal */}
      {showNotificationSettings && (
        <NotificationSettings
          onClose={() => setShowNotificationSettings(false)}
        />
      )}

      {showChangePassword && (
        <ChangePassword
          user={user}
          onClose={() => setShowChangePassword(false)}
          onSuccess={() => {
            alert('Password changed successfully!')
          }}
        />
      )}

      {showRepBreakdown && selectedAccount && (
        <RepBreakdown
          account={selectedAccount}
          promo={selectedAccountPromo}
          onClose={() => {
            setShowRepBreakdown(false)
            setSelectedAccount(null)
            setSelectedAccountPromo(null)
          }}
        />
      )}

      {showQuarterReset && (
        <QuarterReset
          activeQuarter={activeQuarter}
          onClose={() => setShowQuarterReset(false)}
          onSuccess={() => {
            setShowQuarterReset(false)
            handleRefresh()
          }}
        />
      )}
    </div>
  )
}

export default Dashboard;
